import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { type SelfSchedulingDayStructure, SelfSchedulingPreference } from "../../../utils/models";

const amountOfDaysInWeek = 7;

type Props = {
	daysData: Record<string, SelfSchedulingDayStructure>;
	targetWorkingDaysPerWeek: number;
	onDayClick?: (date: DateTime) => void;
	schedulingPeriod?: { periodStart: DateTime; periodEnd: DateTime };
};

/**
 * Custom hook to manage the state of the self-scheduling calendar.
 * @param onDayClick - Callback function triggered when a day is clicked, receiving the date and updated preference.
 * @param daysData - A record mapping date strings to SelfSchedulingDayStructure objects.
 * @param targetWorkingDaysPerWeek - Target number of working days per week, default is 3, above which a warning may be displayed.
 * @param schedulingPeriod - The scheduling period to display (passed from parent state).
 */
export const useSelfSchedulingCalendarState = ({ onDayClick, daysData, targetWorkingDaysPerWeek, schedulingPeriod }: Props) => {
	const [firstDayOfWeeksWithOvertimeWarning, setFirstDayOfWeeksWithOvertimeWarning] = useState<DateTime[]>([]);
	const [firstDayOfWeeksWithUnderThresholdWarning, setFirstDayOfWeeksWithUnderThresholdWarning] = useState<DateTime[]>([]);

	const firstDate = useMemo<DateTime>(() => {
		if (!schedulingPeriod) return DateTime.now();
		return schedulingPeriod.periodStart;
	}, [schedulingPeriod]);

	const calculatedMaxWeekCount = useMemo<number>(() => {
		if (!schedulingPeriod) return 0;

		const { periodStart, periodEnd } = schedulingPeriod;
		return Math.ceil(periodEnd.diff(periodStart, "weeks").weeks);
	}, [schedulingPeriod]);

	const calendarLabel = useMemo<string>(() => {
		if (!schedulingPeriod) return "";
		return schedulingPeriod.periodStart.toFormat("MMMM yyyy");
	}, [schedulingPeriod]);

	useEffect(() => {
		if (!schedulingPeriod) return;

		const overtimeWeeks: DateTime[] = [];
		const underThresholdWeeks: DateTime[] = [];

		const { periodStart: firstDate, periodEnd: lastDayOfMonth } = schedulingPeriod;

		let currentWeekStart = firstDate;

		while (currentWeekStart <= lastDayOfMonth) {
			let signedUpCount = 0;

			for (let i = 0; i < amountOfDaysInWeek; i++) {
				const currentDate = currentWeekStart.plus({ days: i });
				const dayData = daysData[currentDate.toFormat("yyyy-MM-dd")];

				if (dayData?.dayPreference === SelfSchedulingPreference.WANT_TO_WORK) signedUpCount++;
			}

			if (signedUpCount > targetWorkingDaysPerWeek) overtimeWeeks.push(currentWeekStart);

			if (signedUpCount < targetWorkingDaysPerWeek) underThresholdWeeks.push(currentWeekStart);

			currentWeekStart = currentWeekStart.plus({ weeks: 1 });
		}

		setFirstDayOfWeeksWithOvertimeWarning(overtimeWeeks);
		setFirstDayOfWeeksWithUnderThresholdWarning(underThresholdWeeks);
	}, [daysData, targetWorkingDaysPerWeek, schedulingPeriod]);

	return {
		firstDate,
		calendarLabel,
		handleDayClick: onDayClick || (() => {}),
		amountOfDaysInWeek,
		firstDayOfWeeksWithOvertimeWarning,
		firstDayOfWeeksWithUnderThresholdWarning,
		calculatedMaxWeekCount,
	};
};
